import React, { useRef, useCallback, useState, useEffect } from "react";
import { Camera, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { PhotoInstructionInfo } from "../types";
import { t } from "../locales/nl";
import { downscaleImage, generateThumbnail, refreshFile } from "../utils/imageUtils";

const LazyThumbnail: React.FC<{
  src: string;
  alt: string;
  scrollRootEl: HTMLDivElement | null;
}> = ({ src, alt, scrollRootEl }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      {
        root: scrollRootEl,
        rootMargin: "100px",
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollRootEl]);

  return (
    <div ref={ref} className="w-full h-full">
      {isVisible ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-neutral-200" />
      )}
    </div>
  );
};

interface PhotoUploadProps {
  label: string;
  files: File[];
  onChange: (_files: File[]) => void;
  instruction: PhotoInstructionInfo;
  required?: boolean;
  error?: string;
  isCameraMode: boolean;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  label,
  files,
  onChange,
  instruction,
  required,
  error,
  isCameraMode,
}) => {
  const [showInstructions, setShowInstructions] = React.useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [scrollRootEl, setScrollRootEl] = useState<HTMLDivElement | null>(null);
  const [thumbnails, setThumbnails] = React.useState<Map<File, string>>(
    new Map(),
  );
  const [isProcessing, setIsProcessing] = React.useState(false);
  const abortRef = useRef(false);

  // Stabilize the files array: only update the reference when the actual
  // File objects change (by identity). The parent creates a new array via
  // .filter() on every render, which would otherwise re-trigger the
  // thumbnail generation effect and flood the browser with createObjectURL
  // calls — exhausting resources on mobile and breaking submission.
  const stableFilesRef = useRef<File[]>(files);
  const [stableFiles, setStableFiles] = useState<File[]>(files);

  useEffect(() => {
    const prev = stableFilesRef.current;
    if (prev.length !== files.length || files.some((f, i) => f !== prev[i])) {
      stableFilesRef.current = files;
      setStableFiles(files);
    }
  }, [files]);

  const thumbnailsRef = useRef(thumbnails);
  thumbnailsRef.current = thumbnails;

  const generateThumbnailsForNewFiles = useCallback(
    async (newFiles: File[]) => {
      const filesToProcess = newFiles.filter((f) => !thumbnailsRef.current.has(f));
      if (filesToProcess.length === 0) return;

      setIsProcessing(true);
      abortRef.current = false;

      for (const file of filesToProcess) {
        if (abortRef.current) break;
        try {
          const thumb = await generateThumbnail(file);
          if (!abortRef.current) {
            setThumbnails((prev) => new Map(prev).set(file, thumb));
          }
        } catch {
          if (!abortRef.current) {
            setThumbnails((prev) => new Map(prev).set(file, ""));
          }
        }
      }

      setIsProcessing(false);
    },
    [],
  );

  React.useEffect(() => {
    generateThumbnailsForNewFiles(stableFiles);

    setThumbnails((prev) => {
      const fileSet = new Set(stableFiles);
      let pruned = false;
      for (const key of prev.keys()) {
        if (!fileSet.has(key)) {
          pruned = true;
          break;
        }
      }
      if (!pruned) return prev;
      const next = new Map<File, string>();
      for (const [file, thumb] of prev) {
        if (fileSet.has(file)) {
          next.set(file, thumb);
        }
      }
      return next;
    });

    return () => {
      abortRef.current = true;
    };
  }, [stableFiles, generateThumbnailsForNewFiles]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter((file) => {
        const isValidType = file.type.startsWith("image/");
        if (!isValidType) {
          alert(`File ${file.name} is not a valid image file.`);
        }
        return isValidType;
      });

      const sizeValidFiles = validFiles.filter((file) => {
        const isValidSize = file.size <= 10 * 1024 * 1024;
        if (!isValidSize) {
          alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        }
        return isValidSize;
      });

      if (sizeValidFiles.length > 0) {
        const results = await Promise.all(
          sizeValidFiles.map(async (file) => {
            try {
              // Downscale first to reduce upload payload, then rehydrate to keep
              // iOS blob references healthy after browser restarts.
              const scaled = await downscaleImage(file);
              return await refreshFile(scaled);
            } catch {
              console.warn(`Failed to process ${file.name}, using original`);
              return file;
            }
          }),
        );
        onChange([...files, ...results]);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    onChange(newFiles);
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  return (
    <div className="form-group flex flex-col">
      <div className="flex justify-between items-start mb-1 min-h-[40px]">
        <label className="form-label">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <button
          type="button"
          onClick={toggleInstructions}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          {t.instructions}
        </button>
      </div>
      <div
        className={`photo-upload-container border-2 border-dashed rounded-lg p-2 cursor-pointer transition-colors h-[200px]
          ${error ? "border-red-500 bg-red-50" : "border-neutral-300 hover:border-blue-500 hover:bg-blue-50"}`}
        onClick={handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={
            isCameraMode
              ? "image/*"
              : "image/png,image/jpeg,image/jpg,image/heic,image/heif"
          }
          capture={isCameraMode ? "environment" : undefined}
          className="hidden"
          multiple
        />
        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            {isCameraMode ? (
              <Camera size={32} className="text-neutral-400 mb-2" />
            ) : (
              <ImageIcon size={32} className="text-neutral-400 mb-2" />
            )}
            <p className="text-sm text-neutral-500 text-center">
              {isCameraMode ? t.clickToTakePhoto : t.clickToAddPhotos}
            </p>
            <p className="text-xs text-neutral-400 mt-1">
              Max 10MB per afbeelding
            </p>
          </div>
        ) : (
          <div
            ref={setScrollRootEl}
            className="h-full overflow-y-auto scrollbar-hide py-2"
          >
            <div className="grid grid-cols-2 gap-2 py-4">
              {files.map((file, index) => {
                const thumb =
                  file instanceof File ? thumbnails.get(file) : undefined;
                const isLoading = file instanceof File && !thumbnails.has(file);
                return (
                  <div
                    key={index}
                    className="relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="aspect-square border border-neutral-300 rounded-lg overflow-hidden bg-neutral-100">
                      {isLoading ? (
                        <div className="w-full h-full flex items-center justify-center text-neutral-400">
                          <Loader2 size={24} className="animate-spin" />
                        </div>
                      ) : thumb ? (
                        <LazyThumbnail
                          src={thumb}
                          alt={`Uploaded ${index + 1}`}
                          scrollRootEl={scrollRootEl}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-400">
                          <ImageIcon size={24} />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-md"
                    >
                      <X size={16} className="text-red-500" />
                    </button>
                  </div>
                );
              })}
              <div
                className="aspect-square border-2 border-dashed border-neutral-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                {isCameraMode ? (
                  <Camera size={24} className="text-neutral-500" />
                ) : (
                  <ImageIcon size={24} className="text-neutral-500" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {isProcessing && files.length > 0 && (
        <p className="text-blue-600 text-xs mt-1 flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" />
          Foto&apos;s verwerken... ({thumbnails.size}/{files.length})
        </p>
      )}
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      {showInstructions && (
        <>
          <div className="popup-overlay" onClick={toggleInstructions}></div>
          <div className="popup-instruction">
            <h3 className="text-lg font-medium mb-2">{instruction.title}</h3>
            <p className="text-neutral-600 mb-4">{instruction.description}</p>
            <button
              type="button"
              onClick={toggleInstructions}
              className="btn btn-primary w-full"
            >
              {t.understood}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoUpload;
